import { useCallback, useEffect, useState } from 'react';

import useNavigateWithSearch from '../../../../hooks/useNavigateWithSearch';
import { getEarningsCarousel, getRewardsPaidPlacements } from '../../utils/service';
import {
  REWARD_ID_ROUTE_PARAM,
  STORE_ID_ROUTE_PARAM,
  ROUTE_REWARDS_V2_STORE_REWARD_ID,
  ROUTE_REWARDS_STORE_REWARD_ID,
} from '../../config/routes';
import LoadingBar from '../LoadingBar';
import SliderV3 from '../SliderV3';
import MarketingSliderSlide, { MARKETING_TYPE } from './MarketingSliderSlide';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';

import './MarketingSlider.scss';

const MarketingSlider = ({
  isDeals,
}) => {
  const { isSignedIn, userData } = useFirebaseAuth();
  const navigateWithSearch = useNavigateWithSearch();

  const [marketingData, setMarketingData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const canLoad = isSignedIn && userData?.activeSubscription;

  const loadData = useCallback(async () => {
    setIsLoading(true);

    const data = await Promise.all([
      getRewardsPaidPlacements({
        page: 1,
        pageSize: 3,
        ...(isDeals
          ? { exchangeValueMax: 0 }
          : { exchangeValueMin: 1 }
        ),
      }),
      getEarningsCarousel(),
    ]);

    const {
      data: rewardsData,
      error: rewardsError,
    } = data[0];

    const {
      data: carouselData,
      error: carouselError,
    } = data[1];

    let allData = [];

    if (!rewardsError && rewardsData?.rewards) {
      allData = allData.concat(rewardsData.rewards.map(rewardItem => ({
        rewardItem,
        marketingType: MARKETING_TYPE.REWARDS,
      })));
    }

    if (!carouselError && carouselData) {
      allData = allData.concat(carouselData.map(carouselItem => ({
        carouselItem,
        marketingType: MARKETING_TYPE.CAROUSEL,
      })));
    }

    setMarketingData(allData);
    setIsLoading(false);
  }, []);

  const handleOnClick = useCallback(({ marketingType, rewardItem, carouselItem }) => {
    if (marketingType === MARKETING_TYPE.REWARDS) {
      navigateWithSearch(
        (isDeals ? ROUTE_REWARDS_V2_STORE_REWARD_ID : ROUTE_REWARDS_STORE_REWARD_ID)
          .replace(STORE_ID_ROUTE_PARAM, rewardItem.store?.id)
          .replace(REWARD_ID_ROUTE_PARAM, rewardItem.uid),
      );
    } else if (marketingType === MARKETING_TYPE.CAROUSEL) {
      window.location.href = carouselItem.redirectUrl;
    }
  }, [navigateWithSearch, isDeals]);

  useEffect(() => {
    if (canLoad) {
      return;
    }

    loadData();
  }, [canLoad]);

  if (canLoad) {
    return null;
  }

  return (
    <div
      id="marketing-slider"
      className="MarketingSlider"
    >
      <div className="MarketingSlider__container">
        {isLoading ? (
          <LoadingBar className="MarketingSlider__container-loader" />
        ) : (
          marketingData.length > 0 && (
            <SliderV3
              id="marketing-slider"
              slides={marketingData.map((marketingItem, index) => ({
                key: marketingItem.marketingType === MARKETING_TYPE.CAROUSEL
                  ? marketingItem.carouselItem.name
                  : marketingItem.rewardItem.uid,
                slide: (
                  <MarketingSliderSlide
                    index={index}
                    marketingItem={marketingItem}
                    onClick={handleOnClick}
                  />
                ),
              }))}
              type="loop"
              autoplay
              rewind
              gap="1.5rem"
              slidesPerPage={1}
              fixedWidth="25rem"
              breakpoints={{
                992: {
                  fixedWidth: '18.5rem',
                  gap: '0.5rem',
                },
              }}
            />
          )
        )}
      </div>
    </div>
  );
};

export default MarketingSlider;
